/**
 * The stats engine's admin API (`/stats-engine/*`): the Stats tab, the match
 * tab, runs, numbers and the catalogue. Shapes mirror `omnium_stats.views`;
 * the wire is camelCase because `AdminModel` aliases it.
 */

import { apiFetch } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";
import { getAdminApiBaseUrl } from "@/lib/api/config";

/**
 * Where "Back to Stats" goes from a screen showing one stat.
 *
 * The tab takes three targets: a sport, a competition node, or a fixture. A
 * stat's scope is wider than that — a career counts over a whole sport and a
 * format over a sport and a config, and neither ref is a node id. Sending one
 * as a node is what produced "no such competition node" on the way back from
 * any career or format stat, so anything that is not a node or a match goes to
 * the sport's own tab, which is where those stats are listed anyway.
 */
export function backToStats(sport: string, scopeKind: string, scopeRef: string): string {
  if (scopeKind === "match" && scopeRef) return `/${sport}/stats-engine?kind=fixture&ref=${scopeRef}`;
  if (scopeKind === "node" && scopeRef) return `/${sport}/stats-engine?kind=node&ref=${scopeRef}`;
  return `/${sport}/stats-engine`;
}

export type Dot = "up_to_date" | "stale" | "waiting" | "failed" | "never";

export type StatLine = {
  key: string;
  name: string;
  gives: string;
  scopeKind: string;
  scopeRef: string;
  tier: string;
  status: Dot;
  origin: "sport" | "here";
  lastRunAt: string | null;
  lastRunMs: number | null;
  lastRunStatus: string | null;
  asOf: [number, number] | null;
  reality: [number, number] | null;
  definitionVersion: number;
};

export type Tab = {
  targetKind: "sport" | "node" | "fixture";
  targetRef: string;
  sportCode: string;
  title: string;
  summary: { stats: number; up_to_date: number; waiting: number; failed: number };
  fromSport: StatLine[];
  onlyHere: StatLine[];
};

export type Job = {
  id: number;
  lane: string;
  status: string;
  key: string;
  scopeKind: string;
  scopeRef: string;
  attempts: number;
  queuedAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  error: string | null;
};

export type Runs = {
  depth: Record<string, Record<string, number>>;
  oldestWaitingSeconds: number | null;
  workersAlive: number;
  recentFailures: number;
  jobs: Job[];
};

export type Totals = {
  key: string;
  name: string;
  gives: string;
  scopeKind: string;
  scopeRef: string;
  fields: Record<string, string>;
  rows: Array<Record<string, unknown> & { subject_name: string; computed: Record<string, unknown>; shown: Record<string, unknown> }>;
};

export type FixtureView = {
  fixtureId: string;
  code: string;
  name: string;
  status: string;
  lastSeq: number;
  sportCode: string;
  lines: Array<Record<string, unknown>>;
  stats: StatLine[];
  feeds: Array<{ key: string; scope_kind: string; scope_ref: string; lane: string; where: string }>;
};

export type Card = {
  key: string;
  version: number;
  status: string;
  sport: string;
  name: string;
  description: string;
  scopeKind: string;
  gives: string;
  tier: string;
  reads: string[];
  fields: Record<string, string>;
  example: Record<string, unknown> | null;
  edit: string;
  inPlugin: boolean;
  publishedAt: string | null;
};

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(`${getAdminApiBaseUrl()}/stats-engine${path}`, {
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === "string") message = body.detail;
    } catch {
      /* keep the status line */
    }
    throw new ApiError(message, response.status);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const loadTab = (kind: string, ref: string) => call<Tab>(`/tab/${kind}/${ref}`);
export const loadRuns = (lane?: string) => call<Runs>(`/runs?limit=100${lane ? `&lane=${lane}` : ""}`);
export const loadTotals = (key: string, scopeKind: string, scopeRef: string) =>
  call<Totals>(`/totals?key=${encodeURIComponent(key)}&scopeKind=${scopeKind}&scopeRef=${encodeURIComponent(scopeRef)}`);
export const loadFixture = (id: string) => call<FixtureView>(`/fixture/${id}`);
export const loadCatalogue = (sport?: string) => call<Card[]>(`/catalogue${sport ? `?sport=${sport}` : ""}`);
export const runNow = (key: string, scopeKind: string, scopeRef: string) =>
  call<{ queued: boolean; lock: string }>("/run", { method: "POST", body: JSON.stringify({ key, scopeKind, scopeRef }) });
export const retryJob = (id: number) => call<{ retried: number }>(`/runs/${id}/retry`, { method: "POST" });

/** "41 s ago", "3 min ago" — the runs page reads times, not timestamps. */
export function ago(iso: string | null): string {
  if (!iso) return "—";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

export const DOT_LABEL: Record<Dot, string> = {
  up_to_date: "up to date",
  stale: "behind",
  waiting: "waiting for a run",
  never: "never run",
  failed: "last run failed",
};
